import { createContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User, Session } from '@supabase/supabase-js';
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { useTabVisibility } from './useSupabaseQuery';
import { useProfile, useUpdateProfile } from '@/hooks/useProfile';
import type { AuthContextType } from '@/lib/auth-types';
import { AuthContext } from '@/lib/auth-types';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { supabase } = useSupabaseConnection();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const isTabVisible = useTabVisibility();
  
  // 중복 초기화 방지를 위한 ref 추가 (StrictMode 대응)
  const initializeRef = useRef(false);
  
  // React Query를 사용한 프로필 관리
  const { data: profile } = useProfile(user?.id || null);
  const updateProfileMutation = useUpdateProfile();

  // 프로필 정보가 로드되면 Sentry 사용자 정보 업데이트
  useEffect(() => {
    if (user && profile) {
      setSentryUser({
        id: user.id,
        email: user.email,
        name: user.user_metadata?.name || user.user_metadata?.full_name,
        role: profile.role, // 관리자/일반 사용자 구분
      });
    }
  }, [user, profile]);

  const handleAuthStateChange = useCallback(async (session: Session | null) => {
    setSession(session);
    const currentUser = session?.user ?? null;
    
    setUser((prevUser) => {
      const previousUserId = prevUser?.id;
      
      // 사용자 상태가 변경되었을 때 쿼리 캐시 관리
      if (previousUserId !== currentUser?.id) {
        // 이전 사용자와 새 사용자의 캐시를 모두 무효화하여 깨끗한 상태로 시작
        if (previousUserId) {
          queryClient.removeQueries({ queryKey: ["courses", previousUserId] });
          queryClient.removeQueries({ queryKey: ["profile", previousUserId] });
        }
        if (currentUser?.id) {
          queryClient.invalidateQueries({ queryKey: ["courses", currentUser.id] });
          queryClient.invalidateQueries({ queryKey: ["profile", currentUser.id] });
        }
        // 익명 사용자 캐시도 정리
        queryClient.removeQueries({ queryKey: ["courses", "anonymous"] });
      }
      
      return currentUser;
    });
    
    // Sentry 사용자 컨텍스트 설정
    if (currentUser) {
      setSentryUser({
        id: currentUser.id,
        email: currentUser.email,
        name: currentUser.user_metadata?.name || currentUser.user_metadata?.full_name,
      });
    } else {
      clearSentryUser();
    }
    
    setInitialized((prevInitialized) => {
      if (!prevInitialized) {
        setLoading(false);
        return true;
      }
      return prevInitialized;
    });
  }, [queryClient]);

  useEffect(() => {
    // 이미 초기화된 경우 skip (StrictMode 대응)
    if (initializeRef.current) return;
    
    let isMounted = true;
    initializeRef.current = true;

    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (isMounted) {
          await handleAuthStateChange(session);
        }
      } catch (error) {
        console.error('Error getting session:', error);
        if (isMounted) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (isMounted) {
          await handleAuthStateChange(session);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      // cleanup 시에만 플래그 리셋
      if (!isMounted) {
        initializeRef.current = false;
      }
    };
  }, [handleAuthStateChange, supabase.auth]);

  // CRITICAL FIX: Removed visibility handler to prevent getSession() lock contention
  // Tab visibility changes no longer trigger session refresh to avoid Navigator.locks conflicts
  // Supabase's built-in auth state management will handle session validation automatically

  const signInWithKakao = async (redirectTo?: string) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'kakao',
      options: {
        redirectTo: redirectTo || `${window.location.origin}/`,
      }
    });
    if (error) throw error;
  };

  const signOut = async () => {
    try {
      // 세션이 있는 경우만 signOut 호출
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      } else {
        // 세션이 없다면 로컬 상태만 정리
        setUser(null);
        setSession(null);
        // 프로필은 React Query로 관리되므로 쿼리 무효화
        queryClient.removeQueries({ queryKey: ["profile"] });
      }
    } catch (error) {
      console.error('Error signing out:', error);
      // 세션 관련 에러인 경우 로컬 상태 정리
      if (error?.message?.includes('Auth session missing')) {
        setUser(null);
        setSession(null);
        // 프로필은 React Query로 관리되므로 쿼리 무효화
        queryClient.removeQueries({ queryKey: ["profile"] });
        return; // 에러를 던지지 않고 정상 처리
      }
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<typeof profile>) => {
    if (!user) throw new Error('User not authenticated');
    
    return updateProfileMutation.mutateAsync({
      userId: user.id,
      updates
    });
  };

  const value = {
    user,
    session,
    profile,
    loading,
    isAuthenticated: !!user,
    signInWithKakao,
    signOut,
    updateProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

